import React, { useEffect, useRef, useState } from 'react';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { Target, Upload, CheckCircle2, FileText, Loader2 } from 'lucide-react';

const CATEGORIES = [
  { id: 'agence', label: 'Agences immobilières' },
  { id: 'syndic', label: 'Syndics' },
  { id: 'architecte', label: 'Architectes' },
  { id: 'mairie', label: 'Mairies' },
];

export default function ProspectsSettingsCard() {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(null); // category id or 'convention'
  const refs = useRef({});

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get('/prospects/templates/status');
      setStatus(r.data);
    } catch (e) {
      toast.error("Impossible de charger l'état des modèles");
    }
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const upload = async (file, kind, category) => {
    if (!file) return;
    if (!file.name.toLowerCase().endsWith('.pdf')) {
      toast.error('Format PDF requis');
      return;
    }
    const key = kind === 'convention' ? 'convention' : category;
    setUploading(key);
    try {
      const fd = new FormData(); fd.append('file', file);
      const url = kind === 'convention'
        ? '/prospects/templates/convention'
        : `/prospects/templates/plaquette/${category}`;
      await api.post(url, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success('Modèle importé');
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Échec de l'import");
    }
    setUploading(null);
  };

  if (loading) {
    return (
      <div className="bg-white rounded-2xl border border-slate-200 p-6 flex items-center gap-3">
        <Loader2 className="w-5 h-5 animate-spin text-slate-400" />
        <span className="text-slate-500 text-sm">Chargement des modèles…</span>
      </div>
    );
  }

  const fmtSize = (bytes) => {
    if (!bytes) return '';
    const kb = bytes / 1024;
    return kb < 1024 ? `${Math.round(kb)} Ko` : `${(kb / 1024).toFixed(1)} Mo`;
  };

  return (
    <div className="bg-white rounded-2xl border border-slate-200 p-6">
      <div className="flex items-center gap-3 mb-1">
        <div className="w-10 h-10 rounded-xl bg-slate-900 flex items-center justify-center">
          <Target className="w-5 h-5 text-white" />
        </div>
        <div>
          <h2 className="text-xl font-bold text-slate-900">Prospections — Modèles PDF</h2>
          <p className="text-sm text-slate-500">{"Plaquettes par catégorie + convention apporteur d'affaires."}</p>
        </div>
      </div>

      <div className="mt-5 space-y-4">
        <div>
          <h3 className="text-xs uppercase tracking-wider text-slate-500 font-semibold mb-2">Plaquettes par catégorie</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {CATEGORIES.map((c) => {
              const t = status?.plaquettes?.[c.id];
              const isUp = !!t;
              return (
                <div key={c.id} className={`border rounded-lg p-3 ${isUp ? 'border-emerald-200 bg-emerald-50/40' : 'border-slate-200'}`}>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      {isUp ? <CheckCircle2 className="w-4 h-4 text-emerald-600" /> : <FileText className="w-4 h-4 text-slate-400" />}
                      <span className="font-semibold text-slate-900 text-sm">{c.label}</span>
                    </div>
                  </div>
                  {isUp && (
                    <p className="text-[11px] text-slate-500 mt-1 truncate">
                      {t.filename} · {fmtSize(t.size)} · {new Date(t.uploaded_at).toLocaleDateString('fr-FR')}
                    </p>
                  )}
                  {!isUp && <p className="text-[11px] text-slate-400 mt-1">Pas encore chargé</p>}
                  <input
                    ref={(el) => { refs.current[c.id] = el; }}
                    type="file"
                    accept="application/pdf"
                    className="hidden"
                    onChange={(e) => { upload(e.target.files?.[0], 'plaquette', c.id); try { e.target.value = ''; } catch (_) { /* noop */ } }}
                  />
                  <Button
                    size="sm"
                    variant="outline"
                    className="mt-2 w-full"
                    onClick={() => refs.current[c.id]?.click()}
                    disabled={uploading === c.id}
                    data-testid={`upload-plaquette-${c.id}`}
                  >
                    {uploading === c.id ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Upload className="w-3.5 h-3.5 mr-1.5" />}
                    {isUp ? 'Remplacer' : 'Importer le PDF'}
                  </Button>
                </div>
              );
            })}
          </div>
        </div>

        <div>
          <h3 className="text-xs uppercase tracking-wider text-slate-500 font-semibold mb-2">{"Convention apporteur d'affaires"}</h3>
          {(() => {
            const t = status?.convention;
            const isUp = !!t;
            return (
              <div className={`border rounded-lg p-3 ${isUp ? 'border-emerald-200 bg-emerald-50/40' : 'border-slate-200'}`}>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    {isUp ? <CheckCircle2 className="w-4 h-4 text-emerald-600" /> : <FileText className="w-4 h-4 text-slate-400" />}
                    <span className="font-semibold text-slate-900 text-sm">Modèle de convention (PDF)</span>
                  </div>
                </div>
                {isUp && (
                  <p className="text-[11px] text-slate-500 mt-1 truncate">
                    {t.filename} · {fmtSize(t.size)} · {new Date(t.uploaded_at).toLocaleDateString('fr-FR')}
                  </p>
                )}
                {!isUp && <p className="text-[11px] text-slate-400 mt-1">{"Pas encore chargé · requis pour l'envoi de signature aux prospects"}</p>}
                <input
                  ref={(el) => { refs.current.convention = el; }}
                  type="file"
                  accept="application/pdf"
                  className="hidden"
                  onChange={(e) => { upload(e.target.files?.[0], 'convention'); try { e.target.value = ''; } catch (_) { /* noop */ } }}
                />
                <Button
                  size="sm"
                  variant="outline"
                  className="mt-2"
                  onClick={() => refs.current.convention?.click()}
                  disabled={uploading === 'convention'}
                  data-testid="upload-convention-template"
                >
                  {uploading === 'convention' ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Upload className="w-3.5 h-3.5 mr-1.5" />}
                  {isUp ? 'Remplacer' : 'Importer le PDF'}
                </Button>
              </div>
            );
          })()}
        </div>
      </div>
    </div>
  );
}
